import _ from "lodash";
import React, { useState, useEffect } from "react";
import classNames from "classnames";

import classes from "./AIFeedbackStrip.scss";
import {
  ThumbsDownFilled,
  ThumbsDownOutlined,
  ThumbsUpFilled,
  ThumbsUpOutlined,
  InformationOutlined,
} from "@toddle-edu/ds-icons";
import { Tooltip } from "@toddle-edu/ds-web";

import AIFeedbackModal from "./components/AIFeedbackModal";
import AIDetailedPromptModal from "./components/AIDetailedPromptModal";
import {
  updateAIFeedbackComment,
  upsertAIFeedbackStatus,
} from "./modules/AIFeedbackStripModule";
import { connect } from "react-redux";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { useI18n } from "Hooks";
import ACLStore from "lib/aclStore";
import { setToastMsg } from "Login/modules/LoginModule";

const AIFeedbackStrip = props => {
  const {
    defaultFeedbackStatus,
    usageMetricsId,
    showWarningText,
    upsertAIFeedbackStatus,
    updateAIFeedbackComment,
    containerStyle,
    isAIDebugModeEnabled,
    setToastMsg,
    sourceFrom,
    model,
    temperature,
    intentUsageMetricsId,
    showFeedbackStripV2 = false,
    iconContainerStyle,
    showLikeButton,
    showDislikeButton,
    handleIsFeedbackModalOpen,
    viewSource = "",
    isDisabled,
    testId,
  } = props;

  const [feedbackStatus, setFeedbackStatus] = useState(defaultFeedbackStatus);
  const [modalObj, setModalObj] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setFeedbackStatus(defaultFeedbackStatus);
  }, [defaultFeedbackStatus]);

  const { t } = useI18n(["ai", "common"]);

  const isResponseLiked = feedbackStatus === "LIKE";
  const isResponseDisliked = feedbackStatus === "DISLIKE";

  const updateModalState = ({ modalObj }) => {
    setModalObj(modalObj);
    handleIsFeedbackModalOpen?.({ state: !_.isEmpty(modalObj) });
  };

  const onModalClose = () => {
    updateModalState({ modalObj: {} });
  };

  const feedbackHandler = async ({ feedback, isFeedbackSkipped, status }) => {
    try {
      setIsLoading(true);
      const aiFeedbackStatusResponse = await upsertAIFeedbackStatus({
        feedbackStatusInput: {
          parentId: usageMetricsId,
          parentType: "AI_USAGE_METRICS",
          type: status,
        },
      });

      if (!isFeedbackSkipped) {
        await updateAIFeedbackComment({
          feedbackCommentInput: {
            id: usageMetricsId,
            feedback,
          },
        });
      }
      setIsLoading(false);
      if (aiFeedbackStatusResponse.isSuccess) {
        setFeedbackStatus(status);
      }
      onModalClose();
      setToastMsg({
        msg: "toastMsgs:feedback_successfully_recorded",
        type: "success",
      });
    } catch (e) {
      console.error(e);
    }
  };

  const renderLikeIcon = () => {
    const onLikeButtonClick = () => {
      if (isDisabled) return;

      feedbackHandler({
        isFeedbackSkipped: true,
        status: feedbackStatus === "LIKE" ? null : "LIKE",
      });
    };

    return (
      <Tooltip
        tooltip={t("common:like_response")}
        showArrow={true}
        visible={!isDisabled}
      >
        <span
          className={classNames(classes.iconContainer, {
            [classes.disabledIconContainer]: isDisabled,
          })}
          onClick={onLikeButtonClick}
          onKeyDown={e =>
            (e.key === "Enter" || e.key === " ") && onLikeButtonClick()
          }
          tabIndex={0}
          data-test-id={`${testId}-likeIcon`}
        >
          {isResponseLiked ? (
            <ThumbsUpFilled
              variant="subtle"
              size={showFeedbackStripV2 ? "xxx-small" : "xx-small"}
              disabled={isDisabled}
            />
          ) : (
            <ThumbsUpOutlined
              variant="subtle"
              size={showFeedbackStripV2 ? "xxx-small" : "xx-small"}
              disabled={isDisabled}
            />
          )}
        </span>
      </Tooltip>
    );
  };

  const renderDislikeIcon = () => {
    const onDislikeButtonClick = () => {
      if (isDisabled) return;

      if (isResponseDisliked) {
        feedbackHandler({
          isFeedbackSkipped: true,
          status: null,
        });
      } else {
        updateModalState({ modalObj: { status: "DISLIKE" } });
      }
    };

    return (
      <Tooltip
        tooltip={t("common:dislike_response")}
        showArrow={true}
        visible={!isDisabled}
      >
        <span
          className={classNames(classes.iconContainer, {
            [classes.disabledIconContainer]: isDisabled,
          })}
          onClick={onDislikeButtonClick}
          onKeyDown={e =>
            (e.key === "Enter" || e.key === " ") && onDislikeButtonClick()
          }
          tabIndex={0}
          data-test-id={`${testId}-dislikeIcon`}
        >
          {isResponseDisliked ? (
            <ThumbsDownFilled
              variant="subtle"
              size={showFeedbackStripV2 ? "xxx-small" : "xx-small"}
              disabled={isDisabled}
            />
          ) : (
            <ThumbsDownOutlined
              variant="subtle"
              size={showFeedbackStripV2 ? "xxx-small" : "xx-small"}
              disabled={isDisabled}
            />
          )}
        </span>
      </Tooltip>
    );
  };

  const renderInfoIcon = ({ tooltip, metricsId }) => {
    const onClickInfoIcon = () => {
      if (isDisabled) return;

      updateModalState({
        modalObj: {
          status: "INFO",
          metricsId,
        },
      });
    };

    return (
      <Tooltip tooltip={tooltip} showArrow={true} visible={!isDisabled}>
        <span
          className={classNames(classes.iconContainer, {
            [classes.disabledIconContainer]: isDisabled,
          })}
          onClick={onClickInfoIcon}
          onKeyDown={e =>
            (e.key === "Enter" || e.key === " ") && onClickInfoIcon()
          }
          tabIndex={0}
        >
          <InformationOutlined
            variant="warning"
            size={showFeedbackStripV2 ? "xxx-small" : "xx-small"}
            disabled={isDisabled}
            testId={`${testId}-infoIcon`}
          />
        </span>
      </Tooltip>
    );
  };

  const renderModal = () => {
    const { status, metricsId } = modalObj;
    switch (status) {
      case "LIKE":
      case "DISLIKE":
        return (
          <AIFeedbackModal
            modalObj={modalObj}
            feedbackHandler={feedbackHandler}
            viewSource={viewSource}
            testId={`${testId}-aIFeedback-modal`}
          />
        );
      case "INFO":
        return (
          <AIDetailedPromptModal
            usageMetricsId={metricsId}
            onModalClose={onModalClose}
            sourceFrom={sourceFrom}
            model={model}
            temperature={temperature}
            testId={`${testId}-aIDetailedPrompt-modal`}
            messageLimit={props.messageLimit}
            miscInfo={props.miscInfo}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div
        className={classes.container}
        style={containerStyle}
        key={usageMetricsId}
      >
        {showWarningText && (
          <div className={classes.warningText}>
            {showFeedbackStripV2
              ? t("common:ai_response_feedback_text")
              : t("common:ai_response_warning_text")}
          </div>
        )}
        <div className={classes.iconsContainer} style={iconContainerStyle}>
          {showLikeButton ? renderLikeIcon() : null}
          {showDislikeButton ? renderDislikeIcon() : null}
          {isAIDebugModeEnabled && usageMetricsId
            ? renderInfoIcon({
                tooltip: t("ai:ai_response"),
                metricsId: usageMetricsId,
              })
            : null}
          {isAIDebugModeEnabled && intentUsageMetricsId
            ? renderInfoIcon({
                tooltip: t("ai:intent_identification"),
                metricsId: intentUsageMetricsId,
              })
            : null}
        </div>
      </div>
      {!_.isEmpty(modalObj) && renderModal()}
      {isLoading && (
        <FullScreenLoader overlayContainer={classes.overlayContainer} />
      )}
    </>
  );
};

AIFeedbackStrip.defaultProps = {
  defaultFeedbackStatus: "",
  usageMetricsId: "",
  showWarningText: true,
  containerStyle: {},
  showLikeButton: true,
  showDislikeButton: true,
  handleIsFeedbackModalOpen: () => {},
  isDisabled: false,
  testId: "appComponent-aiFeedbackStrip",
};

const mapStateToProps = () => {
  const isAIDebugModeEnabled =
    ACLStore.can("IdentityAccess:EnableAIDebugMode") ||
    ACLStore.can("Common:EnableAIDebugMode");

  return {
    isAIDebugModeEnabled,
  };
};

const mapActionCreators = {
  upsertAIFeedbackStatus,
  updateAIFeedbackComment,
  setToastMsg,
};

export default connect(mapStateToProps, mapActionCreators)(AIFeedbackStrip);
